import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Lottie from "lottie-react";
import * as loadingDots from "../animations/loading-dots.json";
import {
  getDivisionsProgramUnits,
  getFormContainers,
  getFormsyUnsignedSubmissionsByProgramUnit,
} from "./services/soar";
import Select from "./common/Select";
import ScrollToTop from "./buttons/ScrollToTop";
import { lookupFormInfo, getFormList } from "./forms/forms";
import FPApprovals from "./approvals/FPApprovals";

function Approvals() {
  const [programUnitsLoaded, setProgramUnitsLoaded] = useState(false);
  const [submissionsLoaded, setSubmissionsLoaded] = useState(false);
  const [programOptions, setProgramOptions] = useState([]);
  const [programConfigurations, setProgramConfigurations] = useState({});
  const [formContainers, setFormContainers] = useState([]);
  const [formOptions, setFormOptions] = useState([]);
  const [formDescriptions, setFormDescriptions] = useState({});
  const [selectedProgram, setSelectedProgram] = useState();
  const [selectedForm, setSelectedForm] = useState();
  const [unsignedSubmissions, setUnsignedSubmissions] = useState([]);
  const [sortAsc, setSortAsc] = useState(true);
  const [toggleValue, setToggleValue] = useState("Needs Final Signature");
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Route Access Protection
  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    document.title = "SAFY Portal - Approvals";
    return () => {
      document.title = "SAFY Portal";
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      programUnitsLoaded ||
      !safyInfo?.user?.divisions ||
      safyInfo.user.divisions.length === 0
    ) {
      return;
    }

    const loadOptions = async () => {
      setProgramUnitsLoaded(false);
      const response = await Promise.all([
        getFormContainers(),
        getDivisionsProgramUnits(safyInfo.user.divisions),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      const containersResponse = response[0];
      const programUnits = response[1];

      if (containersResponse) {
        let allContainers = [];
        allContainers = JSON.parse(containersResponse).Formsy.Container;
        setFormContainers(allContainers);

        let obj = {};
        allContainers.forEach((value) => {
          obj[value.FormName] = value.FormDescription;
        });
        setFormDescriptions(obj);
      }

      if (programUnits) {
        const formConfigurations = {};
        programUnits.forEach((unit) => {
          if (!unit.sor_safyportalconfig || unit.ccx_expdate) {
            return;
          }

          try {
            formConfigurations[unit.ccx_name] = JSON.parse(
              unit.sor_safyportalconfig
            );
          } catch {
            return;
          }
        });
        setProgramConfigurations(formConfigurations);

        const programOptions = programUnits.map((element) => {
          return {
            _id: element.ccx_name,
            name: element.ccx_name,
          };
        });
        setProgramOptions(programOptions);
      }

      setProgramUnitsLoaded(true);
    };

    loadOptions();
  }, [safyInfo, programUnitsLoaded]);

  const handleSelectedProgramChange = ({ currentTarget: input }) => {
    setSelectedProgram(input.value);

    if (!input.value) {
      return;
    }

    const loadUnsignedSubmissions = async () => {
      setSubmissionsLoaded(false);
      const response = await getFormsyUnsignedSubmissionsByProgramUnit(
        input.value
      );

      if (!isMountedRef.current || !response || response.startsWith("error:")) {
        return;
      }

      const parsedResponse = JSON.parse(response);

      if (!parsedResponse?.Submissions) {
        return [];
      }

      const submissionsNeedingFinalSigned =
        parsedResponse.Submissions.Submission.filter(
          (sub) => sub.Active === "1" && sub.Signatures
        );
      setUnsignedSubmissions(submissionsNeedingFinalSigned);
      setSubmissionsLoaded(true);
    };

    loadUnsignedSubmissions();

    let formNames = [];
    const forms = programConfigurations[input.value]?.forms;

    if (forms?.length > 0) {
      const formsForGroup = forms.map((form) => {
        const groupKey = Object.keys(form)[0];
        const config = form[groupKey];
        return config;
      });
      formNames = formNames.concat(...formsForGroup);
    }

    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    formNames = formNames.filter(onlyUnique);

    const options = formContainers
      .filter((container) => {
        if (container.Active !== "1") {
          return false;
        }

        return (
          formNames.filter((name) => name.includes(container.FormName))[0] !==
          undefined
        );
      })
      .sort((a, b) => a.FormDescription.localeCompare(b.FormDescription))
      .map((container) => {
        const form = getFormList(container.FormName);

        return {
          _id: container.FormName,
          name: form.formName,
        };
      });

    setFormOptions(options);
  };

  const submissionResults = useMemo(() => {
    if (!submissionsLoaded || unsignedSubmissions.length === 0) {
      return [];
    }

    let submissions = [...unsignedSubmissions];

    if (selectedForm) {
      submissions = submissions.filter((sub) => sub.FormName === selectedForm);
    }

    const directionModifier = sortAsc ? 1 : -1;

    submissions.sort((a, b) => {
      const aDate = moment(a.CreatedOn);
      const bDate = moment(b.CreatedOn);

      const sortValue = aDate.isAfter(bDate)
        ? 1
        : aDate.isBefore(bDate)
        ? -1
        : 0;
      return sortValue * directionModifier;
    });

    return submissions;
  }, [submissionsLoaded, unsignedSubmissions, selectedForm, sortAsc]);

  const submissionMarkup = useMemo(() => {
    if (submissionResults.length === 0) {
      return (
        <>
          <div className="row border-bottom">
            <div className="col py-2">No form submissions found...</div>
          </div>
        </>
      );
    }

    const getLastSignedBy = (sub) => {
      if (Array.isArray(sub.Signatures.signatures)) {
        const providerSignatures = [];
        const canvasSignatures = [];

        sub.Signatures.signatures.forEach((sig) => {
          if (sig.signatureType === "Provider") providerSignatures.push(sig);

          if (sig.signatureType === "Canvas") canvasSignatures.push(sig);
        });

        if (providerSignatures.length > 0) {
          const mostRecent = providerSignatures.sort((a, b) => {
            const aDate = moment(a.datetime);
            const bDate = moment(b.datetime);

            if (aDate.isBefore(bDate)) return 1;
            if (aDate.isAfter(bDate)) return -1;
            return 0;
          })[0];

          return mostRecent.name;
        }

        if (canvasSignatures.length > 0) {
          const mostRecent = canvasSignatures.sort((a, b) => {
            const aDate = moment(a.datetime);
            const bDate = moment(b.datetime);

            if (aDate.isBefore(bDate)) return 1;
            if (aDate.isAfter(bDate)) return -1;
            return 0;
          })[0];

          return mostRecent.name;
        }
      } else {
        return sub.Signatures.signatures.name;
      }
    };

    return submissionResults.map((sub) => {
      // const lookupFormInfo = (formName, versionId, versionNumber = 1) => {
      //   const name =
      //     versionNumber > 1 ? `${formName}v${versionNumber}` : formName;
      //   const formInfo = getFormList(name);

      //   if (formInfo && formInfo.formVersionId !== versionId) {
      //     return lookupFormInfo(formName, versionId, versionNumber + 1);
      //   }

      //   return formInfo;
      // };

      const formInfo = lookupFormInfo(sub.FormName, sub.FormVersionId);

      if (!formInfo?.formPath) {
        return null;
      }

      let formPath = formInfo.formPath;

      if (formPath !== "form" && formInfo.formVersionNumber > 1) {
        formPath += `v${formInfo.formVersionNumber}`;
      }

      return (
        <div key={sub.SubmissionId} className="row border-bottom">
          <div className="py-2 border-end col">{sub.ContactName}</div>
          <div className="py-2 border-end col">
            {formDescriptions[sub.FormName]}
          </div>
          <div className="py-2 border-end col">
            {moment(sub.CreatedOn).format("l LT")}
          </div>
          <div className="py-2 border-end col">{getLastSignedBy(sub)}</div>
          <div className="py-2 col-1">
            <Link
              to={`/${formPath}/${sub.FormVersionId}/${sub.ContactId}/${sub.SubmissionId}`}
              target="_blank"
            >
              <i className="fas fa-folder-open"></i>
            </Link>
          </div>
        </div>
      );
    });
  }, [submissionResults, formDescriptions]);

  const encounterApprovalUrl =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "https://ccplive-cp.safy.org/dist/EncounterPortal/index.html?showSignEncounters=true&tabName=FinalSignEncounters"
      : "https://d365base-cp.safy.org/dist/EncounterPortal/index.html?showSignEncounters=true";
  const recordedServiceApprovalUrl =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "https://ccplive-cp.safy.org/dist/EncounterPortal/index.html?showSignEncounters=true&tabName=SignRecordedServices"
      : "https://d365base-cp.safy.org/dist/EncounterPortal/index.html?showSignEncounters=true";
  const alabamaFPApprovalUrl =
    "https://safyreports.azurewebsites.net/Approvals/AlabamaFP_Services";
  const nevadaFPApprovalUrl =
    "https://safyreports.azurewebsites.net/Approvals/NevadaFP_Services";
  const southCarolinaFPApprovalUrl =
    "https://safyreports.azurewebsites.net/Approvals/SouthCarolinaFP_Services";

  return (
    <>
      <div className="mb-5">
        {safyInfo.user.modules.includes("Approvals") ? (
          <>
            <div>
              <button
                className="btn btn-safy-info m-2"
                onClick={() => {
                  window.open(encounterApprovalUrl, "EncounterApproval");
                }}
              >
                Encounter Approval Portal
              </button>
            </div>
            <div>
              <button
                className="btn btn-safy-info m-2"
                onClick={() => {
                  window.open(recordedServiceApprovalUrl, "EncounterApproval");
                }}
              >
                Recorded Service Approval Portal
              </button>
            </div>
            {safyInfo.user.safyInfo.BusinessUnitIdName === "SAFY of Alabama" ||
            safyInfo.user.safyInfo.BusinessUnitIdName === "SAFY of Nevada" ||
            safyInfo.user.safyInfo.BusinessUnitIdName ===
              "SAFY of South Carolina" ? (
              <div>
                <button
                  className={`btn btn-safy-info m-2${
                    toggleValue === "FP Approvals" ? " brighter" : ""
                  }`}
                  onClick={() => {
                    setToggleValue("FP Approvals");
                  }}
                >
                  FP Approval Portal
                </button>
              </div>
            ) : null}
          </>
        ) : null}
        {(safyInfo.user.safyInfo.BusinessUnitIdName === "ccplive" ||
          safyInfo.user.modules.includes("Admin")) && (
          <>
            <button
              className={`btn btn-safy-info m-2${
                toggleValue === "Needs Final Signature" ? " brighter" : ""
              }`}
              onClick={() => {
                setToggleValue("Needs Final Signature");
              }}
            >
              Forms needing Final Signature
            </button>
            <button
              className={`btn btn-safy-info m-2${
                toggleValue === "FP Approvals" ? " brighter" : ""
              }`}
              onClick={() => {
                setToggleValue("FP Approvals");
              }}
            >
              FP Approval Portal
            </button>
          </>
        )}
      </div>
      {toggleValue === "FP Approvals" ? (
        <FPApprovals />
      ) : toggleValue === "Needs Final Signature" ? (
        <div className="mb-5">
          <div className="row">
            <div className="col">
              <div className="h4">Forms in need of Final Signature</div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <Select
                name="SelectedProgram"
                label={`${
                  !programUnitsLoaded
                    ? "Loading Program options..."
                    : "Program:"
                }`}
                value={selectedProgram}
                options={programOptions}
                onChange={handleSelectedProgramChange}
                disabled={!programUnitsLoaded}
              />
            </div>
            <div className="col">
              <Select
                name="SelectedForm"
                label="Form:"
                value={selectedForm}
                options={formOptions}
                onChange={({ currentTarget: input }) =>
                  setSelectedForm(input.value)
                }
              />
            </div>
          </div>
          {selectedProgram ? (
            <>
              <div className="container mb-5 border-start border-top border-end">
                <div className="row border-bottom">
                  <div className="py-2 border-end col">Client</div>
                  <div className="py-2 border-end col">Form Description</div>
                  <div
                    className="py-2 border-end col"
                    onClick={() => {
                      setSortAsc(!sortAsc);
                    }}
                  >
                    Created On
                    {sortAsc ? (
                      <i className="fas fa-angle-up ms-2"></i>
                    ) : (
                      <i className="fas fa-angle-down ms-2"></i>
                    )}
                  </div>
                  <div className="py-2 border-end col">Last Signed By</div>
                  <div className="py-2 col-1">Open</div>
                </div>
                {!submissionsLoaded ? (
                  <div className="row border-bottom">
                    <div className="col">
                      <div className="d-flex justify-content-center align-items-center">
                        <h1>Loading Submissions</h1>
                        <Lottie
                          animationData={loadingDots.default}
                          loop={true}
                          autoplay={true}
                          rendererSettings={{
                            preserveAspectRatio: "xMidYMid slice",
                          }}
                          height={120}
                          width={120}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  submissionMarkup
                )}
              </div>
              <div className="floating-toolbox">
                <ScrollToTop />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default Approvals;
